import React, { useState, useEffect } from 'react';
import { getBanks, createBank, updateBank } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Plus } from 'lucide-react';

const BankMaster = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bankName: '', accountNumber: '', ifscCode: '', accountHolderName: '', branchName: '', isActive: true });

  useEffect(() => { loadBanks(); }, []);

  const loadBanks = async () => {
    try { const res = await getBanks(); setBanks(res.data || []); } 
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateBank(editing.id, form);
        showSuccessToast('Bank updated');
      } else {
        await createBank(form);
        showSuccessToast('Bank created');
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ bankName: '', accountNumber: '', ifscCode: '', accountHolderName: '', branchName: '', isActive: true });
      loadBanks();
    } catch (err) { 
      showErrorToast(err.response?.data?.message || 'Failed'); 
    }
  };

  const openEdit = (bank) => {
    setEditing(bank);
    setForm(bank);
    setShowDialog(true);
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Bank Master</h1>
        <Button onClick={() => { setEditing(null); setForm({ bankName: '', accountNumber: '', ifscCode: '', accountHolderName: '', branchName: '', isActive: true }); setShowDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Add Bank
        </Button>
      </div>

      <div className="grid gap-4">
        {banks.map((b) => (
          <Card key={b.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium">{b.bankName}</p>
                <p className="text-sm text-gray-500">{b.accountNumber} • IFSC: {b.ifscCode}</p>
                <p className="text-xs text-gray-400">{b.accountHolderName} • {b.branchName}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={b.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>{b.isActive ? 'Active' : 'Inactive'}</Badge>
                <Button variant="outline" size="sm" onClick={() => openEdit(b)}>Edit</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {banks.length === 0 && <Card><CardContent className="py-8 text-center text-gray-500">No banks configured</CardContent></Card>}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Bank' : 'Add Bank'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Bank Name *</Label><Input value={form.bankName} onChange={(e) => setForm({...form, bankName: e.target.value})} required /></div>
            <div><Label>Account Number *</Label><Input value={form.accountNumber} onChange={(e) => setForm({...form, accountNumber: e.target.value})} required /></div>
            <div><Label>IFSC Code *</Label><Input value={form.ifscCode} onChange={(e) => setForm({...form, ifscCode: e.target.value})} required /></div>
            <div><Label>Account Holder</Label><Input value={form.accountHolderName} onChange={(e) => setForm({...form, accountHolderName: e.target.value})} /></div>
            <div><Label>Branch</Label><Input value={form.branchName} onChange={(e) => setForm({...form, branchName: e.target.value})} /></div>
            {editing && (
              <div className="flex items-center gap-2">
                <Switch checked={form.isActive} onCheckedChange={(c) => setForm({...form, isActive: c})} />
                <Label>Active</Label>
              </div>
            )}
            <Button type="submit" className="w-full">{editing ? 'Update' : 'Create'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankMaster;
